/** Position-plan helpers: background scale calibration and placement of single-line points. */
import {
  newId,
  type Element,
  type Id,
  type Op,
  type Point,
  type Project,
  type Sheet,
  type SheetBackground,
} from '@elec/domain';
import { pointKind, type Analysis } from '@elec/analysis';
import { paperSize } from '@elec/layout';
import { elementBounds, getSymbol } from '@elec/symbols';

/**
 * Scale the sheet background so that the segment a–b measures `meters` in reality at the
 * sheet's drawing scale (1:planScale). Point a stays where it is.
 */
export function calibrateOps(project: Project, sheetId: Id, a: Point, b: Point, meters: number): Op[] {
  const sheet = project.sheets[sheetId];
  const bg = sheet?.background;
  const paperMm = Math.hypot(b.x - a.x, b.y - a.y);
  if (!sheet || !bg || paperMm <= 0 || !(meters > 0)) return [];
  const scale = sheet.planScale ?? 50;
  const target = (meters * 1000) / scale;
  const f = target / paperMm;
  const next: SheetBackground = {
    ...bg,
    x: a.x - (a.x - bg.x) * f,
    y: a.y - (a.y - bg.y) * f,
    width: bg.width * f,
    height: bg.height * f,
  };
  return [{ op: 'update', coll: 'sheets', id: sheetId, patch: { background: next } }];
}

export interface UnplacedPoint {
  tag: string;
  symbolId: string;
  props: Record<string, string | number | boolean>;
  circuitCode: string;
}

/** Single-line points (with a code) that are not yet on any position plan. */
export function unplacedPoints(project: Project, analysis: Analysis): UnplacedPoint[] {
  const plans = new Set(
    Object.values(project.sheets)
      .filter((s) => s.kind === 'position-plan')
      .map((s) => s.id),
  );
  const placed = new Set(
    Object.values(project.elements)
      .filter((e) => plans.has(e.sheetId) && pointKind(e))
      .map((e) => e.tag),
  );
  const out: UnplacedPoint[] = [];
  for (const c of analysis.circuits)
    for (const p of c.points)
      if (p.element.tag && !placed.has(p.element.tag))
        out.push({
          tag: p.element.tag,
          symbolId: p.element.symbolId,
          props: { ...p.element.props },
          circuitCode: c.code,
        });
  return out;
}

// ---------- drafting a position plan (assistant) ----------

/** A room of a drafted plan and the codes of the points it holds (A1, B3…). */
export interface DraftRoom {
  name: string;
  /** One of ROOM_TYPES (@elec/symbols): decides the wet-location checks. */
  roomType: string;
  tags: string[];
}

export interface PlanResult {
  ops: Op[];
  sheetId: Id;
  /** Codes placed in a room (draft) or next to their circuit (existing plan). */
  placed: string[];
  /** Codes put aside for the user to move to their place. */
  aside: string[];
}

const STEP = 15;
const PER_ROW = 5;
const ROOM_W = PER_ROW * STEP + 10;
const GAP = 8;
const roomHeight = (n: number) => 14 + Math.max(1, Math.ceil(n / PER_ROW)) * STEP + 4;

const pointEl = (sheetId: Id, u: UnplacedPoint, x: number, y: number): Element => ({
  id: newId(),
  sheetId,
  symbolId: u.symbolId,
  x,
  y,
  rotation: 0,
  mirrored: false,
  tag: u.tag,
  props: { ...u.props },
});

/**
 * Positions in the band left of the title block, bottom row first, then rows above: where
 * points wait to be moved to their place, clear of an imported floor plan's middle.
 */
function* asideCells(sheet: Pick<Sheet, 'paper' | 'orientation'>) {
  const { w, h } = paperSize(sheet);
  const right = w - 10 - 180 - 5;
  const perRow = Math.max(1, Math.floor((right - 20) / STEP));
  for (let row = 0; ; row++)
    for (let i = 0; i < perRow; i++) yield { x: 20 + i * STEP, y: h - 24 - row * STEP };
}

/**
 * A new position plan (A3 landscape, 1:50) with a box per room holding its points, as the site
 * survey draws one; the diagram's points that are in no room are put aside for the user. Only
 * points not yet on a plan are drawn, each once (the first room naming it keeps it).
 */
export function draftPlanOps(
  project: Project,
  analysis: Analysis,
  rooms: DraftRoom[],
  name: string,
): PlanResult {
  const todo = unplacedPoints(project, analysis);
  const byTag = new Map(todo.map((u) => [u.tag, u]));
  const sheet: Sheet = {
    id: newId(),
    name,
    kind: 'position-plan',
    paper: 'A3',
    orientation: 'landscape',
    planScale: 50,
    order: Math.max(-1, ...Object.values(project.sheets).map((s) => s.order)) + 1,
  };
  const { w: pw, h: ph } = paperSize(sheet);
  const area = { x: 20, y: 28, w: pw - 40, h: ph - 28 - 50 };
  const ops: Op[] = [{ op: 'add', coll: 'sheets', value: sheet }];
  const placed: string[] = [];
  const taken = new Set<string>();
  let x = area.x;
  let y = area.y;
  let rowH = 0;
  for (const r of rooms) {
    const cells = r.tags.filter((t) => byTag.has(t) && !taken.has(t));
    if (!cells.length) continue;
    const h = roomHeight(cells.length);
    if (x + ROOM_W > area.x + area.w) {
      x = area.x;
      y += rowH + GAP;
      rowH = 0;
    }
    // No room for this one on the page: its points are put aside.
    if (y + h > area.y + area.h) continue;
    ops.push({
      op: 'add',
      coll: 'elements',
      value: {
        id: newId(),
        sheetId: sheet.id,
        symbolId: 'plan.room',
        x,
        y,
        rotation: 0,
        mirrored: false,
        tag: '',
        props: { name: r.name, roomType: r.roomType, w: ROOM_W, h },
      },
    });
    cells.forEach((t, i) => {
      taken.add(t);
      placed.push(t);
      ops.push({
        op: 'add',
        coll: 'elements',
        value: pointEl(
          sheet.id,
          byTag.get(t)!,
          x + 7.5 + (i % PER_ROW) * STEP,
          y + 14 + Math.floor(i / PER_ROW) * STEP,
        ),
      });
    });
    x += ROOM_W + GAP;
    rowH = Math.max(rowH, h);
  }
  const aside = todo.filter((u) => !taken.has(u.tag));
  const cells = asideCells(sheet);
  for (const u of aside) {
    const c = cells.next().value!;
    ops.push({ op: 'add', coll: 'elements', value: pointEl(sheet.id, u, c.x, c.y) });
  }
  return { ops, sheetId: sheet.id, placed, aside: aside.map((u) => u.tag) };
}

/**
 * Add the diagram points missing from an existing position plan (all, or the codes given): next
 * to a point of the same circuit, inside its room, when there is space; otherwise, and always on
 * a plan with an imported floor plan (where a guess would look like a real position), put aside
 * for the user to move.
 */
export function placeMissingOps(
  project: Project,
  analysis: Analysis,
  sheetId: Id,
  tags?: string[],
): PlanResult {
  const sheet = project.sheets[sheetId];
  if (!sheet) return { ops: [], sheetId, placed: [], aside: [] };
  const want = tags && new Set(tags);
  const todo = unplacedPoints(project, analysis).filter((u) => !want || want.has(u.tag));
  const { w, h } = paperSize(sheet);
  const on = Object.values(project.elements).filter((e) => e.sheetId === sheetId);
  const rooms = on.filter((e) => e.symbolId === 'plan.room');
  const bounds = (e: Element) => elementBounds(getSymbol(e.symbolId)!, e);
  const centres = on
    .filter((e) => e.symbolId !== 'plan.room' && getSymbol(e.symbolId))
    .map((e) => {
      const b = bounds(e);
      return { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 };
    });
  const free = (x: number, y: number) => centres.every((c) => Math.hypot(c.x - x, c.y - y) >= 10);
  const roomOf = (x: number, y: number) =>
    rooms.find((r) => {
      const b = bounds(r);
      return x >= b.minX && x <= b.maxX && y >= b.minY && y <= b.maxY;
    });
  const ops: Op[] = [];
  const placed: string[] = [];
  const aside: string[] = [];
  const cells = asideCells(sheet);
  for (const u of todo) {
    const def = getSymbol(u.symbolId);
    const half = { w: (def?.width ?? 10) / 2, h: (def?.height ?? 10) / 2 };
    let spot: { x: number; y: number } | undefined;
    if (!sheet.background) {
      const mates = on.filter(
        (e) =>
          pointKind(e) && e.tag.startsWith(u.circuitCode) && /^\d+$/.test(e.tag.slice(u.circuitCode.length)),
      );
      for (const m of mates) {
        const b = bounds(m);
        const c = { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 };
        const room = roomOf(c.x, c.y);
        const rb = room && bounds(room);
        for (let ring = 1; ring <= 3 && !spot; ring++)
          for (const [dx, dy] of [
            [1, 0],
            [0, 1],
            [-1, 0],
            [0, -1],
            [1, 1],
            [-1, 1],
            [1, -1],
            [-1, -1],
          ] as const) {
            const x = c.x + dx * ring * 12;
            const y = c.y + dy * ring * 12;
            const inside = rb
              ? x - half.w >= rb.minX &&
                x + half.w <= rb.maxX &&
                y - half.h >= rb.minY + 8 &&
                y + half.h <= rb.maxY
              : x - half.w >= 12 && x + half.w <= w - 12 && y - half.h >= 20 && y + half.h <= h - 40;
            if (inside && free(x, y)) {
              spot = { x, y };
              break;
            }
          }
        if (spot) break;
      }
    }
    if (spot) {
      ops.push({ op: 'add', coll: 'elements', value: pointEl(sheetId, u, spot.x - half.w, spot.y - half.h) });
      placed.push(u.tag);
    } else {
      let c = cells.next().value!;
      while (!free(c.x + half.w, c.y + half.h)) c = cells.next().value!;
      ops.push({ op: 'add', coll: 'elements', value: pointEl(sheetId, u, c.x, c.y) });
      aside.push(u.tag);
      spot = { x: c.x + half.w, y: c.y + half.h };
    }
    centres.push(spot);
  }
  return { ops, sheetId, placed, aside };
}
